import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/config';
import Logo from '../assets/logo2.png';
import countriesData from '../countries.json';


export default function Header() {
  const { t } = useTranslation();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [resetPhone, setResetPhone] = useState('');
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [loadingReset, setLoadingReset] = useState(false);
  const [message, setMessage] = useState('');
  const [redirectMessage, setRedirectMessage] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [country, setCountry] = useState();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'pl', name: 'Polski', flag: '🇵🇱' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  ];

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setCurrentLanguage(langCode);
    localStorage.setItem('gamesbox-language', langCode);
  };

  // Sync currentLanguage with i18n language changes (from auto-detection)
  useEffect(() => {
    const handleLanguageChange = (lng) => {
      setCurrentLanguage(lng);
    };
    
    // Set initial language
    setCurrentLanguage(i18n.language);
    
    // Listen for language changes
    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  // Get dial code from country code
  function getDialCode(countryCode) {
    if (!countryCode) return '';
    const country = countriesData.find(c => c.code.toUpperCase() === countryCode.toUpperCase());
    return country ? country.dial_code : '';
  }

  function getCountry() {
    fetch(`https://dz5yc9ggwa4v9.cloudfront.net/Lambda/GetCountry`)
      .then(response => response.text())
      .then(country => {
        const countryLower = country.toLowerCase();
        setCountry(countryLower);
        sessionStorage.setItem("gamesbox-country-code", countryLower);
        // Pre-fill phone with dial code if empty (not in FR — login sends number as typed unless user adds +country)
        if (!phone && countryLower !== 'fr') {
          const dialCode = getDialCode(country);
          if (dialCode) {
            setPhone(dialCode);
          }
        }
      })
  }

  function normalizePhoneForApi(rawPhone) {
    const countryCode = (country || sessionStorage.getItem("gamesbox-country-code") || '').toUpperCase();
    let phoneToSend = rawPhone.trim().replace(/\s+/g, '');

    // Leading "+" means the user chose an explicit country code — do not prepend residence dial code
    if (phoneToSend.startsWith('+')) {
      return phoneToSend.replace(/\D/g, '');
    }

    // France: do not auto-prepend +33; send digits exactly as entered (user adds country code only if they type +…)
    if (countryCode === 'FR') {
      return phoneToSend.replace(/\D/g, '');
    }

    const dialCode = getDialCode(countryCode);

    if (dialCode && countryCode) {
      const dialCodeDigits = dialCode.replace(/^\+/, '');
      const dialCodeWithPlus = dialCode.startsWith('+') ? dialCode : `+${dialCode}`;

      if (!phoneToSend.startsWith(dialCodeWithPlus) && !phoneToSend.startsWith(dialCodeDigits)) {
        const digits = phoneToSend.replace(/\D/g, '');
        if (!digits.startsWith(dialCodeDigits)) {
          phoneToSend = dialCodeDigits + digits;
        } else {
          phoneToSend = digits;
        }
      } else {
        phoneToSend = phoneToSend.replace(/^\+/, '').replace(/\D/g, '');
        if (!phoneToSend.startsWith(dialCodeDigits)) {
          phoneToSend = dialCodeDigits + phoneToSend;
        }
      }
    } else {
      phoneToSend = phoneToSend.replace(/\D/g, '');
    }
    return phoneToSend;
  }

  function loginWithServiceId(phoneToSend, serviceId) {
    const q = new URLSearchParams({
      phonenumber: phoneToSend,
      password,
      serviceid: String(serviceId),
      type: 'signin',
      country: country || '',
    });
    return fetch(`https://apiv2.mobileartsme.com/login?${q.toString()}`).then((resp) => resp.json());
  }

  function applyPasswordLoginSuccess(response, phoneToSend) {
    if (response.Error === 0 && response.Result.Status === 0) {
      response.Result.Phone = phoneToSend;
      localStorage.setItem('GB_User_Data', JSON.stringify(response.Result));
      window.location.href = '/dashboard/home';
      return true;
    }
    return false;
  }

  function Login(e) {
    e.preventDefault();

    setLoadingLogin(true);

    const phoneToSend = normalizePhoneForApi(phone);

    loginWithServiceId(phoneToSend, 8)
      .then((res8) => {
        if (applyPasswordLoginSuccess(res8, phoneToSend)) return null;
        return loginWithServiceId(phoneToSend, 5053).then((res5053) => ({ res8, res5053 }));
      })
      .then((pair) => {
        if (!pair) return;
        const { res8, res5053 } = pair;
        if (applyPasswordLoginSuccess(res5053, phoneToSend)) return;
        const msg =
          res5053.Error === 0 ? res5053.Result.Description : res5053.Result;
        setMessage(msg ?? (res8.Error === 0 ? res8.Result.Description : res8.Result));
      })
      .catch(() => setMessage('Login failed.'))
      .finally(() => setLoadingLogin(false));
  }
  function handlePhoneChange(e) {
    let value = e.target.value;
    const countryCode = (country || sessionStorage.getItem("gamesbox-country-code") || '').toUpperCase();
    const dialCode = getDialCode(countryCode);

    if (dialCode && countryCode) {
      const dialCodeDigits = dialCode.replace(/^\+/, '');
      const dialCodeWithPlus = dialCode.startsWith('+') ? dialCode : `+${dialCode}`;
      const escapedDialCode = dialCodeDigits.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const duplicatePattern = new RegExp(`^(\\+?${escapedDialCode}){2,}`, 'i');
      const match = value.match(duplicatePattern);
      if (match) {
        value = dialCodeWithPlus + value.substring(match[0].length);
      }
    }

    if (value.startsWith('+')) {
      value = '+' + value.substring(1).replace(/\D/g, '');
    } else {
      value = value.replace(/\D/g, '');
    }

    setPhone(value);
  }

  function LoginNoPass(e) {
    e.preventDefault();
    setLoadingLogin(true);
    setMessage(''); // Clear previous error messages
    setRedirectMessage(''); // Clear previous redirect messages

    // Get IDProvider from URL parameter, fallback to 413
    const urlParams = new URLSearchParams(window.location.search);
    const ProviderID = urlParams.get("IDProvider") || "413";

    const countryCode = (country || sessionStorage.getItem("gamesbox-country-code") || '').toUpperCase();
    let normalizedPhone = normalizePhoneForApi(phone);

    const dialCode = getDialCode(countryCode);
    if (dialCode && countryCode) {
      const dialCodeDigits = dialCode.replace(/^\+/, '');
      if (normalizedPhone.startsWith(dialCodeDigits)) {
        const afterDialCode = normalizedPhone.substring(dialCodeDigits.length);
        const cleanedAfter = afterDialCode.replace(/^0+/, '');
        normalizedPhone = dialCodeDigits + cleanedAfter;
      }
    }

    fetch(`https://apiv2.mobileartsme.com/LoginWithoutPass?IDService=5086&MSISDN=${normalizedPhone}&IDProvider=${ProviderID}`)
      .then(resp => resp.json())
      .then(response => {
        if (response.Error === 0) {
          if (response.Result.Status === 0) {
            response.Result.Phone = normalizedPhone;
            localStorage.setItem('GB_User_Data', JSON.stringify(response.Result));
            window.location.href = '/dashboard/home';
          } else {
            // For Bangladesh: if login failed (response not as expected), show error and redirect message
            if (countryCode === 'BD') {
              const errorMsg = response.Result.Description || 'Login failed';
              setMessage(errorMsg);
              setRedirectMessage('You will be redirected to the subscribe page.');
              const subscribeLink = sessionStorage.getItem("gamesbox-bd-subscribe-link") || "https://lp.games-box.mobi/?deviceId=67384657482647&country=bd&mbidbundle=10016&mbidoffer=15734";
              setTimeout(() => {
                window.location.href = subscribeLink;
              }, 2000); // Redirect after 2 seconds
            } else {
              setMessage(response.Result.Description);
              setRedirectMessage('');
            }
          }
        } else {
          // For Bangladesh: if login failed (response not as expected), show error and redirect message
          if (countryCode === 'BD') {
            const errorMsg = response.Result || 'Login failed';
            setMessage(errorMsg);
            setRedirectMessage('You will be redirected to the subscribe page.');
            const subscribeLink = sessionStorage.getItem("gamesbox-bd-subscribe-link") || "https://lp.games-box.mobi/?deviceId=67384657482647&country=bd&mbidbundle=10016&mbidoffer=15734";
            setTimeout(() => {
              window.location.href = subscribeLink;
            }, 2000); // Redirect after 2 seconds
          } else {
            setMessage(response.Result);
            setRedirectMessage('');
          }
        }
      })
      .catch(() => {
        // For Bangladesh: if login failed (response not as expected), show error and redirect message
        if (countryCode === 'BD') {
          setMessage('Login failed.');
          setRedirectMessage('You will be redirected to the subscribe page.');
          const subscribeLink = sessionStorage.getItem("gamesbox-bd-subscribe-link") || "https://lp.games-box.mobi/?deviceId=67384657482647&country=bd&mbidbundle=10016&mbidoffer=15734";
          setTimeout(() => {
            window.location.href = subscribeLink;
          }, 2000); // Redirect after 2 seconds
        } else {
          setMessage('Login failed.');
          setRedirectMessage('');
        }
      })
      .finally(() => setLoadingLogin(false));
  }

  function ResetPassword(e) {
    e.preventDefault();
    setLoadingReset(true);
    fetch(`https://apiv2.mobileartsme.com/gbox_ForgetPassword?phonenumber=${resetPhone}&apisource=1&language=LB`)
      .then(resp => resp.json())
      .then(response => {
        setResetMessage(response.result)
        setLoadingReset(false);
      })
  }

  useEffect(() => {
    // Initialize country from sessionStorage if available
    const savedCountry = sessionStorage.getItem("gamesbox-country-code");
    if (savedCountry) {
      const countryLower = savedCountry.toLowerCase();
      setCountry(countryLower);
      // Pre-fill phone with dial code if empty (not in FR — see getCountry)
      if (!phone && countryLower !== 'fr') {
        const dialCode = getDialCode(savedCountry);
        if (dialCode) {
          setPhone(dialCode);
        }
      }
    } else {
      getCountry();
    }
  }, [])

  // Sync currentLanguage with i18n language changes (from auto-detection)
  useEffect(() => {
    const handleLanguageChange = (lng) => {
      setCurrentLanguage(lng);
    };
    
    // Set initial language from i18n
    setCurrentLanguage(i18n.language);
    
    // Listen for language changes (when auto-detected from country)
    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);


  return (
    <>
      <nav className="navbar navbar-expand-lg navbar-light">
        <div className="container">
          <img src={Logo} alt="Logo" />
          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNav"
            aria-controls="navbarNav"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav ms-auto align-items-center gap-3">
              <li className="nav-item">
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    isActive ? "nav-link fw-bold text-red text-decoration-none" : "nav-link text-decoration-none text-dark"
                  }
                >
                  {t('header.home')}
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink
                  to="/games"
                  className={({ isActive }) =>
                    isActive ? "nav-link fw-bold text-red text-decoration-none" : "nav-link text-decoration-none text-dark"
                  }
                >
                  {t('header.games')}
                </NavLink>
              </li>
              <li className="nav-item">
                <select
                  className="form-select"
                  value={currentLanguage}
                  onChange={(e) => changeLanguage(e.target.value)}
                  style={{ border: '1px solid #dee2e6', minWidth: '120px', cursor: 'pointer' }}
                  aria-label="Select language"
                >
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </li>
              <li className="nav-item">
                <button className="btn btn-primary" data-bs-toggle="modal" data-bs-target="#loginModal">
                  {t('header.login')}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Login Modal */}
      <div className="modal fade" id="loginModal" tabIndex="-1" aria-labelledby="loginModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content p-3">
            <div className="modal-header" style={{ border: 'none' }}>
              <img src={Logo} width={100} alt="logo" />
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>
       <div className="modal-body">
  <h3>Login</h3>
  <p>Dive Into Over 500 Games With Games Box!</p>

  {((country || sessionStorage.getItem("gamesbox-country-code") || "").toLowerCase() === "bd") ? (
    // Bangladesh: No password form
    <form onSubmit={LoginNoPass}>
      <div className="mb-3">
        <label className="form-label" style={{ fontWeight: '500' }}>Phone Number</label>
        <input
          type="text"
          value={phone}
          placeholder="phone number"
          onChange={handlePhoneChange}
          className="form-control"
          required
        />
        <small className="text-muted d-block mt-1" style={{ fontStyle: 'italic' }}>
          {t('header.phoneCountryCodeHint')}
        </small>
      </div>

      {message && <div className="alert alert-danger">{message}</div>}
      {redirectMessage && <div className="alert alert-info mt-2">{redirectMessage}</div>}

      <button type="submit" className="btn btn-primary w-100 br-full" disabled={loadingLogin}>
        {loadingLogin ? (
          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
        ) : 'Login'}
      </button>
    </form>
  ) : (
    // Default form with password + reset
    <form onSubmit={Login}>
      <div className="mb-3">
        <label className="form-label" style={{ fontWeight: '500' }}>Phone Number</label>
        <input
          type="text"
          value={phone}
          placeholder="phone number"
          onChange={handlePhoneChange}
          className="form-control"
          required
        />
        <small className="text-muted d-block mt-1" style={{ fontStyle: 'italic' }}>
          {t('header.phoneCountryCodeHint')}
        </small>
      </div>
      <div className="mb-3">
        <label className="form-label" style={{ fontWeight: '500' }}>Password</label>
        <input
          type="password"
          value={password}
          placeholder="password"
          onChange={e => setPassword(e.target.value)}
          className="form-control"
          required
        />
      </div>

      {message && <div className="alert alert-danger">{message}</div>}

      <button type="submit" className="btn btn-primary w-100 br-full" disabled={loadingLogin}>
        {loadingLogin ? (
          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
        ) : 'Login'}
      </button>

      <div className="text-center mt-3">
        <button
          type="button"
          className="btn p-0"
          data-bs-toggle="modal"
          data-bs-target="#resetModal"
          data-bs-dismiss="modal"
        >
          Forgot Password?
        </button>
      </div>
    </form>
  )}
</div>
          </div>
        </div>
      </div>

      {/* Reset Password Modal */}
      <div className="modal fade" id="resetModal" tabIndex="-1" aria-labelledby="resetModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content p-3">
            <div className="modal-header" style={{ border: 'none' }}>
              <h5 className="modal-title" id="resetModalLabel">Reset Password</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <div className="modal-body">
              <p>Enter your phone number and we will send you a message with your new password.</p>
              <form onSubmit={ResetPassword}>
                <div className="mb-3">
                  <input type="tel" placeholder="Phone number" value={resetPhone} onChange={e => setResetPhone(e.target.value)} className="form-control" required />
                </div>
                <button type="submit" className="btn btn-primary w-100" disabled={loadingReset}>
                  {loadingReset ? (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  ) : 'Reset Password'}
                </button>

                {resetMessage && <div className="alert alert-danger mt-3">{resetMessage}</div>}

              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
